import {
  Box,
  type BoxProps,
  Input as MUIInput,
  InputLabel,
  // eslint-disable-next-line no-restricted-imports
  Select as MUISelect,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { forwardRef, useEffect, useRef, useState } from "react";
import { Duration } from "@dmca/utils/luxon";
import { mergeSx } from "#hooks/mergeSx";

type UnitTypeLongPlural =
  | "milliseconds"
  | "seconds"
  | "minutes"
  | "hours"
  | "days"
  | "months"
  | "years"
  | "dates";

export interface TimeDurationInputProps extends Omit<BoxProps, "onChange"> {
  direction?: "column" | "row";
  label?: string;
  value: Duration | null;
  timeIntervalOptions?: Exclude<UnitTypeLongPlural, "dates">[];
  defaultTimeInterval?: Exclude<UnitTypeLongPlural, "dates">;
  onChange?: (value: Duration | null) => void;
  error?: boolean;
  helperText?: string;
  errorText?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: "small" | "medium";
}

type OwnerState = {
  focused: boolean;
  disabled: boolean;
  hovered: boolean;
  error: boolean;
};

const FormLabelRoot = styled(InputLabel)<{
  ownerState: OwnerState;
}>(({ theme }) => ({
  transformOrigin: "left top 0px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "calc(133% - 32px)",
  transform: "translate(14px, -9px) scale(0.75)",
  position: "absolute",
  transition: theme.transitions.create(["color", "transform", "max-width"], {
    duration: theme.transitions.duration.shorter,
    easing: theme.transitions.easing.easeOut,
  }),
  left: 0,
  top: 0,
  variants: [
    {
      props: ({ ownerState }) => ownerState.focused && !ownerState.disabled,
      style: {
        color: theme.palette.primary.main,
      },
    },
    {
      props: ({ ownerState }) => ownerState.disabled,
      style: {
        color: theme.palette.text.disabled,
      },
    },
    {
      props: ({ ownerState }) => ownerState.error,
      style: {
        color: theme.palette.error.main,
      },
    },
  ],
}));

const NotchedOutlineRoot = styled("fieldset")<{
  ownerState: OwnerState;
}>(({ theme }) => ({
  textAlign: "left",
  position: "absolute",
  bottom: 0,
  right: 0,
  top: "-5px",
  left: 0,
  margin: 0,
  padding: "0 8px",
  borderColor:
    theme.palette.mode === "light"
      ? "rgba(0,0,0,0.23)"
      : "rgba(255,255,255,0.23)",
  borderRadius: theme.shape.borderRadius,
  borderStyle: "solid",
  borderWidth: 1,
  overflow: "hidden",
  minWidth: "0%",
  pointerEvents: "none",
  variants: [
    {
      props: ({ ownerState }) => ownerState.focused && !ownerState.disabled,
      style: {
        borderWidth: 2,
        borderColor: theme.palette.primary.main,
      },
    },
    {
      props: ({ ownerState }) =>
        ownerState.hovered && !ownerState.focused && !ownerState.disabled,
      style: {
        borderColor: theme.palette.text.primary,
      },
    },
    {
      props: ({ ownerState }) => ownerState.error,
      style: {
        borderColor: theme.palette.error.main,
      },
    },
    {
      props: ({ ownerState }) => ownerState.focused && ownerState.error,
      style: {
        borderWidth: 2,
      },
    },
    {
      props: ({ ownerState }) => ownerState.disabled,
      style: {
        borderColor: theme.palette.action.disabled,
      },
    },
  ],
}));

const NotchedOutlineLegend = styled("legend")<{
  ownerState: { withLabel: boolean; notched?: boolean };
}>(({ theme }) => ({
  float: "unset",
  width: "auto",
  overflow: "hidden",
  variants: [
    {
      props: ({ ownerState }) => !ownerState.withLabel,
      style: {
        padding: 0,
        lineHeight: "11px", // sync with `height` in `legend` styles
        transition: theme.transitions.create("width", {
          duration: 150,
          easing: theme.transitions.easing.easeOut,
        }),
      },
    },
    {
      props: ({ ownerState }) => ownerState.withLabel,
      style: {
        display: "block", // Fix conflict with normalize.css and sanitize.css
        padding: 0,
        height: 11, // sync with `lineHeight` in `legend` styles
        fontSize: "0.75em",
        visibility: "hidden",
        maxWidth: 0.01,
        transition: theme.transitions.create("max-width", {
          duration: 50,
          easing: theme.transitions.easing.easeOut,
        }),
        whiteSpace: "nowrap",
        "& > span": {
          paddingLeft: 5,
          paddingRight: 5,
          display: "inline-block",
          opacity: 0,
          visibility: "visible",
        },
      },
    },
    {
      props: ({ ownerState }) => ownerState.withLabel && ownerState.notched,
      style: {
        maxWidth: "100%",
        transition: theme.transitions.create("max-width", {
          duration: 100,
          easing: theme.transitions.easing.easeOut,
          delay: 50,
        }),
      },
    },
  ],
}));

const InputBaseRoot = styled("div")<{
  ownerState: OwnerState & {
    direction: "column" | "row";
    size?: "small" | "medium";
  };
}>(({ theme, ownerState }) => ({
  color: theme.palette.text.primary,
  lineHeight: "1.4375em",
  boxSizing: "border-box",
  position: "relative",
  cursor: ownerState.disabled ? "default" : "text",
  display: "inline-flex",
  alignItems: "center",
  width: "100%",
  gap: "10px",
  flexDirection: ownerState.direction,
  padding: "12px 14px",
  "& > :not(fieldset)": {
    flex: 1,
    width: "100%",
  },
  variants: [
    {
      props: ({ ownerState: { size } }) => size === "small",
      style: {
        padding: "8px 8px",
      },
    },
  ],
}));

export const TimeDurationInput = forwardRef<any, TimeDurationInputProps>(
  (
    {
      sx,
      direction = "row",
      label,
      onChange,
      onMouseOver,
      onMouseOut,
      onFocus,
      onBlur,
      error = false,
      errorText,
      helperText,
      fullWidth = true,
      value,
      size,
      disabled = false,
      timeIntervalOptions: options = ["seconds", "minutes", "hours", "days"],
      defaultTimeInterval = options[0]!,
      ...rest
    },
    ref
  ) => {
    const [focused, setFocused] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [selectValue, setSelectValue] = useState(defaultTimeInterval);
    const [inputValue, setInputValue] = useState(
      value ? value.as(selectValue).toString() : ""
    );

    const inputRef = useRef<HTMLInputElement>(null);
    const selectRef = useRef<HTMLDivElement>(null);
    const inputBaseRef = useRef<HTMLDivElement>(null);

    const handleMouseOver = (e: React.MouseEvent<HTMLDivElement>) => {
      onMouseOver?.(e);
      setHovered(true);
    };

    const handleMouseOut = (e: React.MouseEvent<HTMLDivElement>) => {
      onMouseOut?.(e);
      if (
        !(
          e.relatedTarget === selectRef.current?.firstChild ||
          e.relatedTarget === inputRef.current ||
          e.relatedTarget === inputBaseRef.current
        )
      ) {
        setHovered(false);
      }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      onFocus?.(e);
      setFocused(true);
    };
    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(e);
      if (
        !(
          e.relatedTarget === selectRef.current?.firstChild ||
          e.relatedTarget === inputRef.current
        )
      ) {
        setFocused(false);
        setHovered(false);
      }
    };

    const handleChange = (
      inputValueInner: string,
      selectValueInner: (typeof options)[number]
    ) => {
      if (inputValueInner && selectValueInner) {
        if (isNaN(Number(inputValueInner))) {
          return;
        }

        const duration = Duration.fromObject({
          [selectValueInner]: Number(inputValueInner),
        });
        if (!isNaN(duration.as("milliseconds"))) {
          onChange?.(duration);
          return;
        }
      }
      onChange?.(null);
    };

    useEffect(() => {
      if (value && !isNaN(value.as("milliseconds"))) {
        setInputValue(value.as(selectValue).toString());
      } else {
        setInputValue("");
      }
    }, [selectValue, value]);

    const ownerState = {
      focused,
      disabled,
      hovered,
      error,
    };

    return (
      <Box
        sx={mergeSx(
          {
            position: "relative",
            display: "inline-flex",
            flexDirection: "column",
            verticalAlign: "top",
            width: fullWidth ? "100%" : "inherit",
            my: size === "small" ? "4px" : "12px",
          },
          sx
        )}
        {...rest}
        ref={ref}
      >
        <FormLabelRoot ownerState={ownerState} size={size}>
          {label}
        </FormLabelRoot>
        <InputBaseRoot
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          ref={inputBaseRef}
          ownerState={{ ...ownerState, direction, size }}
        >
          <MUIInput
            placeholder="1"
            onFocus={handleFocus}
            onBlur={handleBlur}
            inputRef={inputRef}
            error={error}
            value={inputValue}
            disabled={disabled}
            size={size}
            inputMode="numeric"
            slotProps={{
              input: {
                pattern: "[0-9]*",
                inputMode: "numeric",
              },
            }}
            sx={
              size === "small"
                ? {
                    "& .MuiInput-input": {
                      py: 0,
                    },
                  }
                : {}
            }
            onChange={(e) => {
              const value = e.target.value;
              const isValid =
                !isNaN(Number(value)) || value === "" || value === "-";
              if (!isValid) {
                return;
              }
              setInputValue(value);
              handleChange(value, selectValue);
            }}
          />
          <MUISelect
            variant="standard"
            onFocus={handleFocus}
            onBlur={handleBlur}
            value={selectValue}
            ref={selectRef}
            error={error}
            size={size}
            disabled={disabled}
            onChange={(e) => {
              const value = e.target.value as (typeof options)[number];
              setSelectValue(value);
              handleChange(inputValue, value);
            }}
            sx={size === "small" ? { "& .MuiSelect-select": { py: 0 } } : {}}
          >
            {options.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </MUISelect>
          <NotchedOutlineRoot ownerState={ownerState}>
            <NotchedOutlineLegend
              ownerState={{ withLabel: !!label, notched: true }}
            >
              <span>{label}</span>
            </NotchedOutlineLegend>
          </NotchedOutlineRoot>
        </InputBaseRoot>
        {(errorText || helperText) && (
          <FormHelperText error={error} disabled={disabled} sx={{ mx: "14px" }}>
            {errorText || helperText}
          </FormHelperText>
        )}
      </Box>
    );
  }
);
